// One country's waves (0043), under the Klaviyo-átadás table: who is in which
// wave and whether Klaviyo was told, what the current N lets through and what
// N + 1 would add; the automatic emc_wave write; and a wave from a Klaviyo
// engagement segment — a dry run first, then a job the klaviyo-sync loop runs.
// Reading and the dry run are everybody's; the job, its stop and the switch
// are an admin's (the API refuses anybody else). Raising N stays in the
// handover row's own dialog, with a note.

import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { toast } from "sonner";
import { FlaskConicalIcon, PlayIcon, SquareIcon, XIcon } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";
import {
  cancelWaveJob,
  fetchCountryWaves,
  fetchKlaviyoSegments,
  previewWave,
  requestWave,
  setWaveSync,
} from "@/lib/api";
import type { CountryWaves, KlaviyoSegment, WaveFigure, WaveJob, WavePreview } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { Switch } from "@/components/ui/switch";
import { ActionDialog } from "@/components/emc/ActionDialog";
import { DataTable } from "@/components/emc/DataTable";
import { StatusPill } from "@/components/emc/StatusPill";
import { Callout, ErrorState } from "@/components/emc/states";
import { count, minute } from "@/components/emc/format";
import { Country } from "@/components/emc/Flag";
import { messageOf } from "@/pages/campaigns/model";
import { loopSentence, outcomeRows, progressOf, readinessOf } from "@/pages/warmup/handover";

// While a job is open the panel asks again this often: the loop moves it a
// slice per tick.
const POLL_MS = 5000;
const WAVES = [1, 2, 3, 4, 5, 6, 7];

function useCountryWaves(country: string) {
  const [waves, setWaves] = useState<CountryWaves | null>(null);
  const [error, setError] = useState<string | null>(null);
  const reload = useCallback(async () => {
    try {
      setWaves(await fetchCountryWaves(country));
      setError(null);
    } catch (raised: unknown) {
      setError(messageOf(raised));
    }
  }, [country]);
  useEffect(() => {
    setWaves(null);
    void reload();
  }, [reload]);
  const open = waves?.open_job !== null && waves?.open_job !== undefined;
  useEffect(() => {
    if (!open) return undefined;
    const timer = window.setInterval(() => void reload(), POLL_MS);
    return () => window.clearInterval(timer);
  }, [open, reload]);
  return { waves, error, reload };
}

function Outcome({ values, done }: { values: Parameters<typeof outcomeRows>[0]; done: boolean }) {
  return (
    <dl className="grid grid-cols-2 gap-x-6 gap-y-1 sm:grid-cols-4">
      {outcomeRows(values, done).map((row) => (
        <div key={row.label} className="flex flex-col">
          <dt className="text-label text-muted-foreground">{row.label}</dt>
          <dd className="m-0 data-mono font-semibold">{count(row.value)}</dd>
        </div>
      ))}
    </dl>
  );
}

function JobLine({ job }: { job: WaveJob }) {
  const percent = progressOf(job);
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <StatusPill domain="waveJob" state={job.state} />
        <span>
          {job.wave}. hullám ← „{job.segment_name}”
        </span>
        <span className="text-label text-muted-foreground">
          {job.requested_by} · <span className="data-mono">{minute(job.requested_at)}</span>
        </span>
      </div>
      {percent !== null && job.state !== "done" && (
        <div
          role="meter"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent}
          aria-valuetext={`${count(job.done)} / ${count(job.total)} tag`}
          className="h-1.5 w-full overflow-hidden rounded-pill bg-fill"
        >
          <div
            className="h-full rounded-pill bg-linear-to-r from-info to-primary transition-[width]"
            style={{ width: `${percent}%` }}
          />
        </div>
      )}
      <Outcome values={job.result} done />
      {job.error && job.state !== "done" && (
        <p className="text-label text-muted-foreground">{job.error}</p>
      )}
    </div>
  );
}

export function WavesPanel({
  country,
  isAdmin,
  onClose,
  onChanged,
}: {
  country: string;
  isAdmin: boolean;
  onClose: () => void;
  /** The handover list shows the switch and the open job; tell it to reload. */
  onChanged: () => void;
}) {
  const { waves, error, reload } = useCountryWaves(country);
  const [segments, setSegments] = useState<KlaviyoSegment[] | null>(null);
  const [wave, setWave] = useState("1");
  const [segmentId, setSegmentId] = useState("");
  const [preview, setPreview] = useState<WavePreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [asking, setAsking] = useState<"job" | "stop" | "sync_on" | null>(null);
  const waveId = useId();
  const segmentFieldId = useId();

  useEffect(() => {
    let alive = true;
    setSegments(null);
    setSegmentId("");
    setPreview(null);
    fetchKlaviyoSegments(country)
      .then((rows) => alive && setSegments(rows))
      .catch((raised: unknown) => alive && toast(messageOf(raised)));
    return () => {
      alive = false;
    };
  }, [country]);

  // A dry run is about one wave and one segment; another pick needs another.
  useEffect(() => setPreview(null), [wave, segmentId]);

  const chosen = useMemo(
    () => (segments ?? []).find((row) => String(row.segment_id) === segmentId) ?? null,
    [segments, segmentId],
  );

  function changed(message: string) {
    toast(message);
    setAsking(null);
    void reload();
    onChanged();
  }

  async function runPreview() {
    if (!chosen) return;
    setPreviewing(true);
    try {
      setPreview(await previewWave(country, Number(wave), chosen.segment_id));
    } catch (raised: unknown) {
      toast(messageOf(raised));
    } finally {
      setPreviewing(false);
    }
  }

  async function turnOff() {
    try {
      await setWaveSync(country, false);
      changed(`${country}: az emc_wave automatikus írása kikapcsolva.`);
    } catch (raised: unknown) {
      toast(messageOf(raised));
    }
  }

  const columns: ColumnDef<WaveFigure>[] = [
    { id: "wave", header: "Hullám", cell: ({ row }) => <span className="data-mono">{row.original.wave}.</span> },
    ...(
      [
        ["assigned", "Kijelölve"],
        ["synced", "A Klaviyo tudja"],
        ["pending", "Írásra vár"],
        ["unsyncable", "Nem írható"],
        ["mailable", "Küldhető"],
      ] as const
    ).map(
      ([key, header]): ColumnDef<WaveFigure> => ({
        id: key,
        header,
        cell: ({ row }) => <span className="data-mono">{count(row.original[key])}</span>,
        meta: { align: "right" },
      }),
    ),
  ];

  const open = waves?.open_job ?? null;
  const readiness = waves ? readinessOf(waves) : null;
  const pending = waves?.totals.pending ?? 0;

  return (
    <section
      aria-label={`${country} hullámai`}
      className="flex flex-col gap-5 rounded-card border border-separator p-4"
    >
      <header className="flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 font-title">
          <Country code={country} /> hullámai
        </h3>
        <Button size="sm" variant="ghost" type="button" onClick={onClose} aria-label="Bezárás">
          <XIcon />
        </Button>
      </header>

      {error !== null && <ErrorState message={error} onRetry={() => void reload()} />}

      {waves && (
        <>
          {readiness && <Callout tone="info">{readiness}</Callout>}
          <DataTable
            label={`${country} hullámai`}
            variant="plain"
            columns={columns}
            data={waves.waves}
            getRowId={(item) => String(item.wave)}
          />
          {waves.totals.unsyncable > 0 && (
            <p className="text-label text-muted-foreground">
              Nem írható: nincs Klaviyo-profilja, vagy már nincs a fiókban. Nekik nem küldünk az
              átadáson át, és a Klaviyo sem tudja kizárni őket.
            </p>
          )}

          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-3">
              <Switch
                id={`${waveId}-sync`}
                checked={waves.sync.enabled}
                disabled={!isAdmin}
                onCheckedChange={(next) => (next ? setAsking("sync_on") : void turnOff())}
              />
              <Label htmlFor={`${waveId}-sync`}>emc_wave automatikus írása a Klaviyóba</Label>
            </div>
            <p className="text-muted-foreground">{loopSentence(waves.sync)}</p>
            <p className="text-label text-muted-foreground">
              Írásra vár: <span className="data-mono">{count(pending)}</span>
              {waves.sync.changed_by && (
                <>
                  {" "}
                  · {waves.sync.enabled ? "bekapcsolta" : "kikapcsolta"} {waves.sync.changed_by},{" "}
                  <span className="data-mono">{minute(waves.sync.changed_at)}</span>
                </>
              )}
              {waves.sync.loop_seen_at && (
                <>
                  {" "}
                  · a klaviyo-sync utoljára:{" "}
                  <span className="data-mono">{minute(waves.sync.loop_seen_at)}</span>
                </>
              )}
            </p>
          </div>

          <div className="flex flex-col gap-3">
            <h4 className="font-title">Hullám kijelölése Klaviyo-szegmensből</h4>
            {open ? (
              <div className="flex flex-col gap-3">
                <JobLine job={open} />
                {isAdmin && (
                  <span>
                    <Button size="sm" variant="outline-danger" type="button" onClick={() => setAsking("stop")}>
                      <SquareIcon />
                      Leállítás
                    </Button>
                  </span>
                )}
              </div>
            ) : (
              <>
                <div className="flex flex-wrap items-end gap-3">
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor={waveId}>Hullám</Label>
                    <NativeSelect id={waveId} value={wave} onChange={(event) => setWave(event.target.value)}>
                      {WAVES.map((number) => (
                        <option key={number} value={String(number)}>
                          {number}.
                        </option>
                      ))}
                    </NativeSelect>
                  </div>
                  <div className="flex min-w-60 flex-col gap-1.5">
                    <Label htmlFor={segmentFieldId}>Klaviyo-szegmens</Label>
                    <NativeSelect
                      id={segmentFieldId}
                      value={segmentId}
                      disabled={segments === null}
                      onChange={(event) => setSegmentId(event.target.value)}
                    >
                      <option value="">{segments === null ? "Betöltés…" : "Válassz szegmenst"}</option>
                      {(segments ?? []).map((row) => (
                        <option key={row.segment_id} value={String(row.segment_id)} disabled={!row.refreshed_at}>
                          {row.name} — {count(row.member_count)} tag
                          {row.refreshed_at ? "" : " (még nincs frissítve)"}
                          {row.gone_at ? " (a Klaviyóban már nincs)" : ""}
                        </option>
                      ))}
                    </NativeSelect>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    type="button"
                    disabled={!chosen || previewing}
                    onClick={() => void runPreview()}
                  >
                    <FlaskConicalIcon />
                    Próba
                  </Button>
                  {isAdmin && (
                    <Button size="sm" type="button" disabled={!preview} onClick={() => setAsking("job")}>
                      <PlayIcon />
                      Kijelölés
                    </Button>
                  )}
                </div>
                {segments !== null && segments.length === 0 && (
                  <p className="text-muted-foreground">
                    Ennek az országnak még nincs követett Klaviyo engagement-szegmense.
                  </p>
                )}
                {preview && (
                  <div className="flex flex-col gap-2">
                    <p className="text-label text-muted-foreground">
                      Próba: {preview.wave}. hullám a(z) „{preview.segment_name}” tagjaiból, a{" "}
                      <span className="data-mono">{minute(preview.refreshed_at)}</span> frissítés szerint
                      {preview.from_later_wave > 0 &&
                        ` — ebből ${count(preview.from_later_wave)} egy későbbi hullámból jön előrébb, az emc_wave-jüket újra kell írni`}
                      .
                    </p>
                    <Outcome values={preview} done={false} />
                  </div>
                )}
              </>
            )}

            {waves.jobs.filter((job) => job.id !== open?.id).length > 0 && (
              <details>
                <summary className="cursor-pointer text-label text-muted-foreground">Korábbi kijelölések</summary>
                <div className="mt-3 flex flex-col gap-4">
                  {waves.jobs
                    .filter((job) => job.id !== open?.id)
                    .map((job) => (
                      <JobLine key={job.id} job={job} />
                    ))}
                </div>
              </details>
            )}
          </div>
        </>
      )}

      <ActionDialog
        open={asking === "sync_on"}
        onOpenChange={(next) => !next && setAsking(null)}
        title={`${country}: emc_wave automatikus írása`}
        description={
          <>
            Bekapcsolva a klaviyo-sync minden kijelölt, még nem értesített kontakt Klaviyo-profiljára
            felírja az <span className="data-mono">emc_wave</span> értékét. A Klaviyo-kampányok
            ezzel zárják ki őket, amint N eléri a hullámukat. Csak akkor ír, ha a globális
            Klaviyo-írás (<span className="data-mono">EMC_KLAVIYO_PUSH_ENABLED</span>) is engedélyezve
            van. Most {count(pending)} kontakt vár írásra.
          </>
        }
        confirmLabel="Bekapcsolás"
        onConfirm={async () => {
          await setWaveSync(country, true);
          changed(`${country}: az emc_wave automatikus írása bekapcsolva.`);
        }}
      />

      {preview && (
        <ActionDialog
          open={asking === "job"}
          onOpenChange={(next) => !next && setAsking(null)}
          title={`${country}: ${preview.wave}. hullám kijelölése`}
          description={
            <>
              A(z) „{preview.segment_name}” {count(preview.matched)} tagjából{" "}
              {count(preview.stamped)} kerül a(z) {preview.wave}. hullámba. Aki már korábbi hullámban
              van, ott marad. A kijelölés a háttérben fut, itt látszik, hol tart. A Klaviyóba csak az
              automatikus írás viszi át, és küldeni csak akkor kezdünk nekik, ha N eléri a hullámot.
            </>
          }
          confirmLabel="Kijelölés"
          onConfirm={async () => {
            await requestWave(country, preview.wave, preview.segment_id);
            setPreview(null);
            changed(`${country}: a ${preview.wave}. hullám kijelölése elindult.`);
          }}
        />
      )}

      {open && (
        <ActionDialog
          open={asking === "stop"}
          onOpenChange={(next) => !next && setAsking(null)}
          title={`${country}: kijelölés leállítása`}
          description={
            <>
              Aki eddig a(z) {open.wave}. hullámba került, ott marad ({count(open.result.stamped)}{" "}
              kontakt). A maradék nem kerül be; egy új kijelölés folytatja.
            </>
          }
          confirmLabel="Leállítás"
          tone="danger"
          onConfirm={async () => {
            await cancelWaveJob(country, open.id);
            changed(`${country}: a kijelölés leállítva.`);
          }}
        />
      )}
    </section>
  );
}
